import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

const MAX_LENGTH = 2000;

const formatTime = (value) => (value ? new Date(value).toLocaleString('fi-FI', {
  day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

/**
 * The message thread with Amili on this invoice's collection. Loaded only when opened, so
 * viewing an invoice never calls Amili. Our messages on the right, Amili's on the left. A row
 * of its own under the actions, so it does not read as one more action button.
 */
const AmiliMessages = ({ t, invoice, canSend }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  if (!invoice?.amili_assignment_id) return null;
  const url = `/api/invoices/${invoice.id}/amili_messages/`;

  const errorText = (err) => {
    const code = err.response?.data?.error_code;
    const fallback = err.response?.data?.error || t('amili.messagesFailed');
    return code ? t(`amili.error.${code}`, fallback) : fallback;
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(url);
      setMessages(response.data.messages || []);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null) load();
  };

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const response = await axios.post(url, { text: text.trim() });
      setMessages(response.data.messages || []);
      setText('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }} data-testid="amili-messages">
      <Divider />
      <Button
        fullWidth
        color="inherit"
        startIcon={<ForumOutlinedIcon />}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={toggle}
        sx={{ mt: 1, justifyContent: 'flex-start', '& .MuiButton-endIcon': { ml: 'auto' } }}
        aria-expanded={open}
        data-testid="amili-messages-toggle"
      >
        {t('amili.messagesTitle')}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          {loading && <CircularProgress size={20} />}
          {!loading && messages !== null && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">{t('amili.messagesEmpty')}</Typography>
          )}
          {!loading && (messages || []).map((message) => {
            const ours = message.sender_party !== 'agency';
            return (
              <Box
                key={message.uuid || message.created_at}
                sx={{ display: 'flex', justifyContent: ours ? 'flex-end' : 'flex-start', mb: 1 }}
                data-testid={ours ? 'amili-message-ours' : 'amili-message-amili'}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: ours ? 'primary.main' : 'background.paper',
                    color: ours ? 'primary.contrastText' : 'text.primary',
                    border: ours ? 'none' : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                    {ours ? t('amili.messageFromUs') : t('amili.messageFromAmili')} · {formatTime(message.created_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.text}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {error && <Alert severity="error" sx={{ my: 1 }} data-testid="amili-messages-error">{error}</Alert>}
          {canSend && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-end', mt: 1 }}>
              <TextField
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('amili.messagePlaceholder')}
                multiline
                minRows={2}
                size="small"
                sx={{ flex: '1 1 240px', bgcolor: 'background.paper' }}
                inputProps={{ maxLength: MAX_LENGTH, 'data-testid': 'amili-message-input' }}
              />
              <Button
                variant="contained"
                endIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                onClick={send}
                disabled={!text.trim() || sending}
                data-testid="amili-message-send"
              >
                {t('amili.messageSend')}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default withTranslation()(AmiliMessages);
